import { Link, useLocation, useNavigate } from '@tanstack/react-router';
import { ChevronRight, ChevronUp, LogOut, Maximize2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from '@/components/ui/sidebar';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useAuth } from '@/lib/auth-state';
import {
  isActivePath,
  KIOSK_TOOLTIP,
  NAV,
  type NavEntry,
  type NavGroup as NavGroupDef,
  type NavLeaf,
  SETTINGS,
  visibleChildren,
} from '@/lib/nav';
import type { Role } from '@/lib/roles';

type LeafEntry = Extract<NavEntry, { kind: 'leaf' }>;

/** Which groups the operator has opened or closed by hand, valid for ONE pathname (see below). */
type GroupOverrides = { pathname: string; open: Record<string, boolean> };

export function AppSidebar() {
  const location = useLocation();
  const pathname = location.pathname;
  const { setOpenMobile } = useSidebar();
  const { state } = useAuth();
  const role = state.status === 'signed-in' ? state.role : undefined;

  // On a phone the sidebar is a Sheet. It used to close after a tap only as a side-effect of the shell
  // remounting on every route change; now that the shell is a layout route and persists (router.tsx
  // `_shell`), the close has to be deliberate — otherwise the menu would sit open over the page the
  // operator just navigated to. Runs on mount too, which is a no-op (the sheet starts closed).
  // biome-ignore lint/correctness/useExhaustiveDependencies: the pathname is the TRIGGER (any navigation closes the sheet), not a value the effect reads
  useEffect(() => {
    setOpenMobile(false);
  }, [pathname, setOpenMobile]);

  // A group is open when it holds the current page. A click on its header overrides that — until the
  // next navigation, when the override is dropped and the rule reasserts itself. Deliberately NOT
  // persisted: a remembered "closed" on the group that holds the page you just navigated to would hide
  // the active row, and a remembered "open" on every group is the flat list this replaced.
  const [overrides, setOverrides] = useState<GroupOverrides>({ pathname, open: {} });
  const isOpen = (group: NavGroupDef): boolean => {
    const manual = overrides.pathname === pathname ? overrides.open[group.label] : undefined;
    return manual ?? group.children.some((c) => isActivePath(c, pathname));
  };
  const setOpen = (group: NavGroupDef, open: boolean) =>
    setOverrides((prev) => ({
      pathname,
      open: { ...(prev.pathname === pathname ? prev.open : {}), [group.label]: open },
    }));

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <div className="flex items-center gap-2 group-data-[collapsible=icon]:justify-center">
          <img
            src="/fsp-logo.png"
            alt="Fast Screen Print"
            className="size-9 shrink-0 rounded-md object-contain"
          />
          <div className="flex flex-col min-w-0 group-data-[collapsible=icon]:hidden">
            <span className="truncate text-sm font-semibold leading-tight">Fast Screen Print</span>
            <span className="truncate text-xs text-muted-foreground leading-tight">
              Internal dashboard
            </span>
          </div>
        </div>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {NAV.map((entry) =>
                entry.kind === 'leaf' ? (
                  <NavLink key={entry.label} item={entry} currentPath={pathname} />
                ) : (
                  <NavGroup
                    key={entry.label}
                    group={entry}
                    role={role}
                    currentPath={pathname}
                    open={isOpen(entry)}
                    onOpenChange={(open) => setOpen(entry, open)}
                  />
                ),
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        {/* Settings: pinned to the bottom of the scrollable content (the shadcn secondary-nav idiom),
            collapsed unless the current page is one of its rows. */}
        <SidebarGroup className="mt-auto">
          <SidebarGroupContent>
            <SidebarMenu>
              <NavGroup
                group={SETTINGS}
                role={role}
                currentPath={pathname}
                open={isOpen(SETTINGS)}
                onOpenChange={(open) => setOpen(SETTINGS, open)}
              />
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter>
        <UserMenu />
      </SidebarFooter>
    </Sidebar>
  );
}

function NavLink({ item, currentPath }: { item: LeafEntry; currentPath: string }) {
  const Icon = item.icon;
  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild isActive={isActivePath(item, currentPath)} tooltip={item.label}>
        <Link to={item.to}>
          <Icon />
          <span>{item.label}</span>
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

/**
 * A group of rows under one header. Expanded, it is a Collapsible tree (the shadcn NavMain pattern).
 * Collapsed to the icon rail, nested rows are hidden by the primitive itself (`SidebarMenuSub` is
 * `group-data-[collapsible=icon]:hidden`), so the header opens a menu beside the rail instead — same
 * rows, same order. The phone Sheet is never in icon mode, so it always gets the tree.
 */
function NavGroup({
  group,
  role,
  currentPath,
  open,
  onOpenChange,
}: {
  group: NavGroupDef;
  role: Role | undefined;
  currentPath: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { state, isMobile } = useSidebar();
  const Icon = group.icon;
  const children = visibleChildren(group, role);
  const containsActive = children.some((c) => isActivePath(c, currentPath));

  if (state === 'collapsed' && !isMobile) {
    return (
      <SidebarMenuItem>
        <DropdownMenu>
          {/* Tooltip OUTSIDE the trigger: `SidebarMenuButton tooltip=…` would put a context provider,
              not a DOM node, under the trigger's Slot, and the menu would never open. */}
          <Tooltip>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton isActive={containsActive}>
                  <Icon />
                  <span>{group.label}</span>
                </SidebarMenuButton>
              </DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent side="right" align="center">
              {group.label}
            </TooltipContent>
          </Tooltip>
          <DropdownMenuContent side="right" align="start" sideOffset={4} className="min-w-44">
            <DropdownMenuLabel className="text-xs text-muted-foreground">
              {group.label}
            </DropdownMenuLabel>
            {children.map((child) => (
              <DropdownMenuItem key={child.to} asChild>
                <Link to={child.to} title={child.kiosk ? KIOSK_TOOLTIP : undefined}>
                  <span className="flex-1">{child.label}</span>
                  <KioskMark kiosk={child.kiosk} />
                </Link>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    );
  }

  return (
    <Collapsible asChild open={open} onOpenChange={onOpenChange} className="group/collapsible">
      <SidebarMenuItem>
        <CollapsibleTrigger asChild>
          {/* Lit when it holds the current page but is folded shut, so the operator can still see where
              they are; open, the row itself carries the highlight instead. */}
          <SidebarMenuButton tooltip={group.label} isActive={containsActive && !open}>
            <Icon />
            <span>{group.label}</span>
            <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
          </SidebarMenuButton>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <SidebarMenuSub>
            {children.map((child) => (
              <SidebarMenuSubItem key={child.to}>
                <SidebarMenuSubButton asChild isActive={isActivePath(child, currentPath)}>
                  <Link to={child.to} title={child.kiosk ? KIOSK_TOOLTIP : undefined}>
                    <span>{child.label}</span>
                    <KioskMark kiosk={child.kiosk} />
                  </Link>
                </SidebarMenuSubButton>
              </SidebarMenuSubItem>
            ))}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  );
}

/** The "this opens full screen" marker on a kiosk row: a glyph for the eye, a phrase for a screen reader. */
function KioskMark({ kiosk }: { kiosk: NavLeaf['kiosk'] }) {
  if (!kiosk) return null;
  return (
    <>
      <Maximize2 className="ml-auto size-3.5 shrink-0 text-sidebar-foreground/60" aria-hidden />
      <span className="sr-only">(opens full screen)</span>
    </>
  );
}

function UserMenu() {
  const { state, signOut } = useAuth();
  const navigate = useNavigate();
  const email = state.status === 'signed-in' ? state.email : '';
  // Cosmetic role label from the ID token's groups — not an auth signal (see auth-state / roles).
  const roleLabel = state.status === 'signed-in' ? state.role.label : 'Signed in';
  const initials = email ? email.slice(0, 2).toUpperCase() : 'FS';

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <div className="flex size-8 items-center justify-center rounded-md bg-sidebar-primary text-xs font-semibold text-sidebar-primary-foreground">
                {initials}
              </div>
              <div className="grid flex-1 text-left text-sm leading-tight min-w-0">
                <span className="truncate font-medium">{email || 'Signed in'}</span>
                <span className="truncate text-xs text-muted-foreground">{roleLabel}</span>
              </div>
              <ChevronUp className="ml-auto size-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            side="top"
            align="end"
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56"
          >
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col gap-0.5">
                <span className="text-sm font-medium">{email || 'Signed in'}</span>
                <span className="text-xs text-muted-foreground">Fast Screen Print</span>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => {
                void signOut().then(() => navigate({ to: '/login' }));
              }}
            >
              <LogOut />
              <span>Sign out</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
